<template>
  <div class="scoring-detail-page">
    <div class="header">
      <button class="back-btn" @click="goBack">
        <i class="el-icon-arrow-left"></i> 返回
      </button>
      <h1>评分详情</h1>
    </div>

    <div class="content" v-if="scoreData">
      <div class="triple-row">
        <!-- 1. 客观风险能力（权重 40%） -->
        <div class="section-card">
          <div class="section-header">
            <span class="section-num">1.</span>
            <span class="section-title">客观风险能力</span>
            <span class="section-weight">（40%）</span>
          </div>
          <div class="metric-row">
            <div class="ring-chart">
              <svg viewBox="0 0 100 100" class="ring-svg">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" stroke-width="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#3b82f6" stroke-width="8"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="getRingOffset(scoreData.abilityPercentScore)" stroke-linecap="round"
                        transform="rotate(-90 50 50)" />
              </svg>
              <span class="ring-value">{{ scoreData.abilityPercentScore }}%</span>
            </div>
            <div class="metrics">
              <div class="metric-item">
                <span class="metric-label">原始得分</span>
                <span class="metric-value">{{ scoreData.abilityRawScore }} / {{ scoreData.abilityMaxScore }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">百分制得分率</span>
                <span class="metric-value">{{ scoreData.abilityPercentScore }}%</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">加权贡献</span>
                <span class="metric-value highlight">{{ scoreData.abilityWeightedContribution }}分</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. 主观风险意愿（权重 60%） -->
        <div class="section-card">
          <div class="section-header">
            <span class="section-num">2.</span>
            <span class="section-title">主观风险意愿</span>
            <span class="section-weight">（60%）</span>
          </div>
          <div class="metric-row">
            <div class="ring-chart">
              <svg viewBox="0 0 100 100" class="ring-svg">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" stroke-width="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#8b5cf6" stroke-width="8"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="getRingOffset(scoreData.willingnessPercentScore)" stroke-linecap="round"
                        transform="rotate(-90 50 50)" />
              </svg>
              <span class="ring-value">{{ scoreData.willingnessPercentScore }}%</span>
            </div>
            <div class="metrics">
              <div class="metric-item">
                <span class="metric-label">原始得分</span>
                <span class="metric-value">{{ scoreData.willingnessRawScore }} / {{ scoreData.willingnessMaxScore
                }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">百分制得分率</span>
                <span class="metric-value">{{ scoreData.willingnessPercentScore }}%</span>
              </div>
              <div class="metric-item">
                <span class="metric-label">加权贡献</span>
                <span class="metric-value highlight">{{ scoreData.willingnessWeightedContribution }}分</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. 加权汇总与定级 -->
        <div class="section-card">
          <div class="section-header">
            <span class="section-num">3.</span>
            <span class="section-title">加权汇总与定级</span>
          </div>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">加权总分</span>
              <span class="summary-value highlight">{{ scoreData.weightedTotalScore }} / 100</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">初始评级</span>
              <span class="summary-value">{{ scoreData.initialRiskLevel }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">最终评级</span>
              <span class="summary-value highlight">{{ scoreData.finalRiskLevel }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. 合规修正 -->
      <div class="warning-card" v-if="scoreData.triggeredVetoRules && scoreData.triggeredVetoRules.length">
        <div class="warning-header">
          <span class="section-num">4.</span>
          <span class="section-title">合规修正</span>
          <i class="warning-icon">!</i>
        </div>
        <div class="warning-content">
          <ul class="veto-list">
            <li v-for="(rule, idx) in scoreData.triggeredVetoRules" :key="idx">{{ rule }}</li>
          </ul>
        </div>
      </div>

      <!-- Tab页签 -->
      <div class="section-card">
        <div class="tabs">
          <button v-for="tab in tabs" :key="tab.key" :class="['tab-btn', { active: activeTab === tab.key }]"
                  @click="switchTab(tab.key)">
            {{ tab.label }}
          </button>
        </div>

        <!-- 单题得分账单 -->
        <div v-show="activeTab === 'bill'" class="tab-content" ref="tableScrollEl" @scroll="handleTableScroll">
          <div class="table-header">
            <span class="table-title">单题得分账单</span>
            <span class="table-count">共{{ scoreData.questionDetails?.length || 0 }}题</span>
          </div>
          <div class="table-container">
            <table class="score-table">
              <thead>
                <tr>
                  <th>题号</th>
                  <th>用户答案</th>
                  <th>得分</th>
                  <th>最高分</th>
                  <th>维度</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in scoreData.questionDetails" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td class="answer-cell">{{ item.answerText }}</td>
                  <td>{{ item.score }}</td>
                  <td>{{ item.maxScore }}</td>
                  <td>{{ item.dimension }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <!-- 评分逻辑说明 -->
        <div v-show="activeTab === 'logic'" class="tab-content">
          <div class="logic-cards">
            <div class="logic-card">
              <div class="logic-step">1</div>
              <div class="logic-body">客观风险能力按25分折算为百分制后乘以40%</div>
            </div>
            <div class="logic-card">
              <div class="logic-step">2</div>
              <div class="logic-body">主观风险意愿按35分折算为百分制后乘以60%</div>
            </div>
            <div class="logic-card">
              <div class="logic-step">3</div>
              <div class="logic-body">最终评分 = 初始评级 + 红线合规审查结果</div>
            </div>
          </div>
        </div>

        <!-- 规则与画像 -->
        <div v-show="activeTab === 'rulesAndImage'" class="tab-content rules-image-tab">
          <div class="rules-image-wrapper">
            <div class="rules-section">
              <div class="section-title-small">评分规则</div>
              <div class="risk-scale">
                <div class="scale-track">
                  <div v-for="level in riskLevels" :key="level.key"
                       :class="['scale-item', level.key, { active: level.key === scoreData.finalRiskLevel }]">
                    <div class="scale-tag">{{ level.key }}</div>
                    <div class="scale-name">{{ level.name }}</div>
                    <div class="scale-range">{{ scoreData.riskLevelBoundaries[level.key] }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="image-section">
              <div class="section-title-small">画像计算结果</div>
              <div class="profile-grid">
                <div class="profile-card" v-for="item in profileItems" :key="item.key">
                  <div class="profile-icon" v-html="item.icon"></div>
                  <div class="profile-info">
                    <span class="profile-label">{{ item.label }}</span>
                    <span class="profile-value">{{ scoreData.profileCalculationRules[item.key] }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="empty" v-else>
      <p>暂无评分数据</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PingfenXiangqing',
  data () {
    return {
      scoreData: null,
      circumference: 2 * Math.PI * 42,
      activeTab: 'rulesAndImage',
      tabs: [
        { key: 'rulesAndImage', label: '规则与画像' },
        { key: 'bill', label: '单题得分账单' },
        { key: 'logic', label: '评分逻辑说明' },
      ],
      riskLevels: [
        { key: 'C1', name: '保守型' },
        { key: 'C2', name: '稳健型' },
        { key: 'C3', name: '平衡型' },
        { key: 'C4', name: '成长型' },
        { key: 'C5', name: '进取型' }
      ],
      profileItems: [
        { key: 'ExpectedAnnualReturn', label: '期望收益率', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>' },
        { key: 'MaxAcceptableDrawdown', label: '可接受最大回撤', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>' },
        { key: 'InvestmentHorizonYears', label: '投资期限', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>' },
        { key: 'LiquidityNeed', label: '流动性需求', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22a7 7 0 0 0 7-7c0-5.5-7-13-7-13S5 9.5 5 15a7 7 0 0 0 7 7z"/></svg>' },
        { key: 'BenchmarkIndex', label: '对标指数代码', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>' }
      ],
      syncChannel: null,
      syncGuard: false,
      rafId: null,
      remoteApplyTime: 0,
      lastSentRatio: -1
    }
  },
  created () {
    this.loadData()
    this.initSyncChannel()
  },
  beforeDestroy () {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    if (this.syncChannel) {
      this.syncChannel.close()
      this.syncChannel = null
    }
  },
  methods: {
    loadData () {
      const scoreDataStr = localStorage.getItem('riskResult') || sessionStorage.getItem('riskResult')
      if (scoreDataStr) {
        try {
          this.scoreData = JSON.parse(scoreDataStr)?.scoringDetails
          return
        } catch (e) {
          console.error('解析 scoreDetail 失败', e)
        }
      }
      // this.scoreData = this.getDefaultData()
    },

    /**
     * 初始化主副屏同步通道（BroadcastChannel）
     * 用于 Tab 切换同步 + 表格滚动位置同步
     * 滚动同步采用时间戳防抖机制替代 syncGuard，彻底消除反馈循环导致的抽动
     */
    initSyncChannel () {
      try {
        this.syncChannel = new BroadcastChannel('pingfen-xiangqing-sync')
        this.syncChannel.onmessage = (e) => {
          const msg = e.data
          if (!msg) return

          if (msg.type === 'tab') {
            this.syncGuard = true
            this.activeTab = msg.key
            this.$nextTick(() => { this.syncGuard = false })
          }

          if (msg.type === 'scroll') {
            // 无条件接收最新滚动位置，不做 syncGuard 过滤
            this.applyRemoteScroll(msg.ratio)
          }
        }
      } catch (e) {
        console.warn('【评分详情】BroadcastChannel 初始化失败:', e.message)
      }
    },

    /**
     * Tab 切换（触发 → 广播到副屏）
     */
    switchTab (key) {
      this.activeTab = key
      if (this.syncChannel && !this.syncGuard) {
        this.syncGuard = true
        this.syncChannel.postMessage({ type: 'tab', key })
        this.$nextTick(() => { this.syncGuard = false })
      }
    },

    /**
     * 表格容器滚动事件（RAF 节流 + 时间戳防抖）
     * 核心策略：本地滚动 200ms 内若刚应用过远程滚动，则抑制广播，
     * 彻底切断"远程应用 → 本地滚动事件 → 广播回去 → 又来远程消息"的反馈循环
     */
    handleTableScroll () {
      if (this.rafId) return
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null
        if (this.activeTab !== 'bill' || !this.syncChannel) return
        // 时间戳防抖：刚应用远程滚动 200ms 内不广播，阻断反馈循环
        if (Date.now() - this.remoteApplyTime < 200) return
        const el = this.$refs.tableScrollEl
        if (!el) return
        const maxScroll = el.scrollHeight - el.clientHeight
        if (maxScroll <= 0) return
        const ratio = el.scrollTop / maxScroll
        // 跳过重复比例，避免无意义广播
        if (Math.abs(ratio - this.lastSentRatio) < 0.002) return
        this.lastSentRatio = ratio
        this.syncChannel.postMessage({ type: 'scroll', ratio })
      })
    },

    /**
     * 接收远程滚动比例 → 按当前容器高度等比还原
     * 记录 remoteApplyTime 时间戳，让 handleTableScroll 200ms 内抑制广播
     */
    applyRemoteScroll (ratio) {
      if (this.activeTab !== 'bill') return
      this.remoteApplyTime = Date.now()
      const el = this.$refs.tableScrollEl
      if (!el) return
      const maxScroll = el.scrollHeight - el.clientHeight
      if (maxScroll <= 0) return
      const target = Math.round(ratio * maxScroll)
      // 阈值：位置变化 < 3px 跳过，避免微抖动
      if (Math.abs(el.scrollTop - target) < 3) return
      el.scrollTop = target
    },

    /**
     * 默认/示例数据，结构与后端接口返回一致：
     *   abilityRawScore / abilityMaxScore / abilityPercentScore / abilityWeightedContribution
     *   willingnessRawScore / willingnessMaxScore / willingnessPercentScore / willingnessWeightedContribution
     *   weightedTotalScore / initialRiskLevel / finalRiskLevel / triggeredVetoRules / questionDetails / logicRules
     */
    getDefaultData () {
      return {
        abilityRawScore: 18,
        abilityMaxScore: 25,
        abilityPercentScore: 72.0,
        abilityWeightedContribution: 28.8,
        willingnessRawScore: 17,
        willingnessMaxScore: 35,
        willingnessPercentScore: 48.6,
        willingnessWeightedContribution: 29.2,
        weightedTotalScore: 58.0,
        initialRiskLevel: 'C3',
        finalRiskLevel: 'C2',
        triggeredVetoRules: [
          '无任何投资经验（Q13选D）；风险承受等级强制封顶为 C2（稳健型）'
        ],
        questionDetails: [
          { id: 'Q1', answerText: '25-35岁', score: 4, maxScore: 5, dimension: 'Ability' },
          { id: 'Q2', answerText: '本科及以上', score: 5, maxScore: 5, dimension: 'Ability' },
          { id: 'Q3', answerText: '个人年收入 30-50万', score: 5, maxScore: 5, dimension: 'Ability' },
          { id: 'Q4', answerText: '金融资产 50-100万', score: 5, maxScore: 5, dimension: 'Ability' },
          { id: 'Q5', answerText: '6-12个月生活储备', score: 5, maxScore: 5, dimension: 'Ability' },
          { id: 'Q6', answerText: '能承受 10-30%的短期亏损', score: 5, maxScore: 5, dimension: 'Ability' },
          { id: 'Q7', answerText: '期望收益率 10-30%/年', score: 2, maxScore: 5, dimension: 'Ability' },
          { id: 'Q8', answerText: '相对谨慎偏好（保守：保守300）', score: 3, maxScore: 5, dimension: 'Willingness' },
          { id: 'Q9', answerText: '可接受波动，追求长期增值', score: 3, maxScore: 5, dimension: 'Willingness' },
          { id: 'Q10', answerText: '可接受波动，追求长期增值', score: 3, maxScore: 5, dimension: 'Willingness' }
        ],
        logicRules: [
          '客观能力按35分折算为百分制后乘以40%；',
          '主观意愿按20分折算为百分制后乘以60%；',
          '最终评级 = 初始评级 + 红线合规减分结果（若触发封顶则以下调为准）；',
          '加权总分 = 客观贡献分(40%) + 主观贡献分(60%)。'
        ]
      }
    },

    /**
     * 计算环形进度条的 stroke-dashoffset
     * @param {number} pct - 百分制得分（0-100 的数字）
     */
    getRingOffset (pct) {
      const ratio = (typeof pct === 'number' ? pct : parseFloat(pct)) / 100
      return this.circumference * (1 - ratio)
    },

    goBack () {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="less">
/* ============================================
   基准样式（≥1200px 桌面端）
   ============================================ */
.scoring-detail-page {
  min-height: 100vh;
  // background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 50%, #f5f0ff 100%);
  background: url('../../assets/bg-hp.png') no-repeat;
  background-size: 100% 100%;
  padding: 20px 24px;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.12);

  h1 {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.8);
  color: #475569;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);

  &:hover {
    background: white;
    border-color: #6366f1;
    color: #6366f1;
  }
}

.content {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ---- 三列并排 ---- */
.triple-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  .section-card {
    min-width: 0;
  }

  .section-header {
    margin-bottom: 10px;
    padding-bottom: 8px;
  }

  .metric-row {
    gap: 14px;
  }

  .ring-chart {
    width: 72px;
    height: 72px;
  }

  .ring-value {
    font-size: 13px;
  }

  .metrics {
    gap: 6px;
  }

  .metric-label {
    font-size: 11px;
  }

  .metric-value {
    font-size: 12px;

    &.highlight {
      font-size: 13px;
    }
  }
}

.section-card {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid rgba(99, 102, 241, 0.06);
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.06);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.12);
  }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.section-num {
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  flex-shrink: 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.section-weight {
  font-size: 12px;
  color: #64748b;
}

.section-hint {
  margin-left: auto;
  font-size: 12px;
  color: #94a3b8;
}

.metric-row {
  display: flex;
  align-items: center;
  gap: 30px;
}

.ring-chart {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.ring-svg {
  width: 100%;
  height: 100%;
}

.ring-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 16px;
  font-weight: 700;
  color: #3b82f6;
}

.metrics {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.metric-label {
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  text-align: right;
  word-break: keep-all;

  &.highlight {
    color: #3b82f6;
    font-size: 15px;
  }
}

.summary-grid {
  display: flex;
  gap: 24px;
}

.summary-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-label {
  font-size: 13px;
  color: #64748b;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #334155;

  &.highlight {
    color: #3b82f6;
    font-size: 18px;
  }
}

/* ---- 合规修正警告条 ---- */
.warning-card {
  background: #fffbeb;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #fcd34d;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(245, 158, 11, 0.1);
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.warning-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f59e0b;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  line-height: 1;
}

.warning-content {
  padding-left: 32px;
}

.veto-list {
  margin: 0;
  padding-left: 18px;

  li {
    font-size: 13px;
    color: #92400e;
    line-height: 1.6;
  }
}

/* ---- Tab 页签 ---- */
.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #f1f5f9;
  margin-bottom: 16px;
  //overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.tab-btn {
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: #3b82f6;
  }

  &.active {
    color: #3b82f6;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: #3b82f6;
    }
  }
}

.tab-content {
  min-height: 200px;
  max-height: 420px;
  overflow-y: auto;
  will-change: scroll-position;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.table-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.table-count {
  font-size: 13px;
  color: #94a3b8;
}

.scroll-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  color: #94a3b8;
}

/* ---- 评分逻辑说明（步骤卡片） ---- */
.logic-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.logic-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
  border-radius: 10px;
  border: 1px solid rgba(59, 130, 246, 0.1);
  transition: all 0.2s;

  &:hover {
    border-color: rgba(59, 130, 246, 0.25);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
  }
}

.logic-step {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.logic-body {
  font-size: 13px;
  color: #334155;
  line-height: 1.7;
}

/* ---- 评分规则（风险等级色阶） ---- */
.risk-scale {
  padding: 4px 0;
}

.scale-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 16px;
}

.scale-track {
  display: flex;
  border-radius: 12px;
  overflow: hidden;
}

.scale-item {
  flex: 1;
  text-align: center;
  padding: 16px 8px 14px;
  position: relative;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    z-index: 1;
  }

  &.C1 {
    background: linear-gradient(180deg, #dbeafe, #bfdbfe);
  }

  &.C2 {
    background: linear-gradient(180deg, #bbf7d0, #86efac);
  }

  &.C3 {
    background: linear-gradient(180deg, #fef08a, #fde047);
  }

  &.C4 {
    background: linear-gradient(180deg, #fed7aa, #fdba74);
  }

  &.C5 {
    background: linear-gradient(180deg, #fecaca, #fca5a5);
  }
}

.scale-tag {
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 4px;
}

.scale-name {
  font-size: 11px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 8px;
}

.scale-range {
  font-size: 11px;
  color: #64748b;
  line-height: 1.5;
}

/* ---- 画像计算结果（信息卡片网格） ---- */
.profile-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
}

.profile-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border-radius: 10px;
  border: 1px solid rgba(139, 92, 246, 0.1);
  transition: all 0.2s;

  &:hover {
    border-color: rgba(139, 92, 246, 0.25);
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.08);
  }
}

.profile-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profile-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.profile-label {
  font-size: 12px;
  color: #7c3aed;
  font-weight: 500;
}

.profile-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-all;
}

/* ---- 规则与画像合并标签样式 ---- */
.rules-image-tab {
  max-height: 500px;
}

.rules-image-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rules-section,
.image-section {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.section-title-small {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.rules-section .risk-scale {
  padding: 0;
}

.rules-section .scale-track {
  border-radius: 12px;
}

.scale-item.active {
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
  border-radius: 12px;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.image-section .profile-grid {
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.image-section .profile-card {
  padding: 12px 14px;
}

.table-container {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.score-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
}

.score-table th,
.score-table td {
  padding: 10px 12px;
  text-align: left;
  font-size: 13px;
  border-bottom: 1px solid #f1f5f9;
}

.score-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

.score-table tbody tr:last-child td {
  border-bottom: none;
}

.score-table tbody tr:hover {
  background: #f8fafc;
}

.answer-cell {
  color: #334155;
  font-weight: 500;
}

.empty {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  font-size: 16px;
}

/* ============================================
   断点 1：900px ~ 1199px（平板横屏 / 小桌面）
   ============================================ */
@media screen and (min-width: 900px) and (max-width: 1199px) {
  .content {
    max-width: 100%;
  }

  .triple-row {
    gap: 10px;

    .ring-chart {
      width: 60px;
      height: 60px;
    }

    .ring-value {
      font-size: 12px;
    }

    .metric-label {
      font-size: 10px;
    }

    .metric-value {
      font-size: 11px;

      &.highlight {
        font-size: 12px;
      }
    }

    .metrics {
      gap: 5px;
    }

    .metric-row {
      gap: 10px;
    }
  }

  .section-card {
    padding: 12px 14px;
  }

  .summary-value {
    font-size: 14px;

    &.highlight {
      font-size: 17px;
    }
  }

  .summary-grid {
    gap: 16px;
  }

  .tab-content {
    max-height: 350px;
  }

  .profile-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

/* ============================================
   断点 2-A：600px ~ 799px（小平板竖屏，如 720×1280）
   单列布局，避免三列卡片内容挤压
   ============================================ */
@media screen and (min-width: 600px) and (max-width: 799px) {
  .scoring-detail-page {
    padding: 16px 20px;
    // background: linear-gradient(180deg, #f0f4ff 0%, #faf5ff 50%, #f0f9ff 100%);
    background: url('../../assets/bg-sp.png') no-repeat;
    background-size: 100% 100%;
  }

  .content {
    max-width: 100%;
    gap: 14px;
  }

  .header {
    margin-bottom: 18px;

    h1 {
      font-size: 20px;
    }
  }

  .triple-row {
    grid-template-columns: 1fr;
    gap: 14px;

    .ring-chart {
      width: 84px;
      height: 84px;
    }

    .ring-value {
      font-size: 15px;
    }

    .metric-label {
      font-size: 13px;
    }

    .metric-value {
      font-size: 14px;

      &.highlight {
        font-size: 15px;
      }
    }

    .metrics {
      gap: 12px;
    }

    .metric-row {
      gap: 24px;
    }

    .section-header {
      margin-bottom: 12px;
      padding-bottom: 10px;
    }
  }

  .section-card {
    padding: 16px 20px;
  }

  .summary-grid {
    gap: 24px;
  }

  .summary-value {
    font-size: 16px;

    &.highlight {
      font-size: 20px;
    }
  }

  .tab-btn {
    padding: 10px 20px;
    font-size: 14px;
  }

  .tab-content {
    max-height: 550px;
  }

  .profile-grid {
    grid-template-columns: 1fr 1fr;
  }

  /* 规则与画像竖屏样式 */
  .rules-image-wrapper {
    flex-direction: column;
    gap: 16px;
  }

  .rules-section,
  .image-section {
    flex: none;
  }

  .rules-section .scale-track {
    border-radius: 10px;
  }

  .scale-item {
    padding: 10px 4px;

    .scale-tag {
      font-size: 16px;
    }

    .scale-name {
      font-size: 10px;
      margin-bottom: 4px;
    }

    .scale-range {
      font-size: 9px;
    }
  }

  .image-section .profile-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .image-section .profile-card {
    padding: 10px 12px;
  }

  .logic-tip-bar {
    padding: 10px 14px;
    border-radius: 6px;

    span {
      font-size: 11px;
    }
  }
}

/* ============================================
   断点 2-B：800px ~ 899px（小平板横屏）
   两列布局，第三列独占一行
   ============================================ */
@media screen and (min-width: 800px) and (max-width: 899px) {
  .scoring-detail-page {
    padding: 14px 16px;
  }

  .content {
    max-width: 100%;
    gap: 12px;
  }

  .header {
    margin-bottom: 16px;

    h1 {
      font-size: 19px;
    }
  }

  .triple-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    .section-card:nth-child(3) {
      grid-column: 1 / -1;
    }

    .ring-chart {
      width: 80px;
      height: 80px;
    }

    .ring-value {
      font-size: 14px;
    }

    .metric-label {
      font-size: 12px;
    }

    .metric-value {
      font-size: 13px;

      &.highlight {
        font-size: 14px;
      }
    }

    .metrics {
      gap: 10px;
    }

    .metric-row {
      gap: 20px;
    }
  }

  .section-card {
    padding: 14px 16px;
  }

  .summary-value {
    font-size: 15px;

    &.highlight {
      font-size: 18px;
    }
  }

  .summary-grid {
    gap: 20px;
  }

  .tab-btn {
    padding: 10px 16px;
  }

  .tab-content {
    max-height: 330px;
  }

  .profile-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* ============================================
   断点 3：< 600px（手机）
   ============================================ */
@media screen and (max-width: 599px) {
  .scoring-detail-page {
    padding: 10px 12px;
    background: linear-gradient(180deg, #f0f4ff 0%, #faf5ff 50%, #f0f9ff 100%);
  }

  .content {
    max-width: 100%;
    gap: 10px;
  }

  .header {
    margin-bottom: 14px;
    gap: 10px;

    h1 {
      font-size: 17px;
    }
  }

  .back-btn {
    padding: 6px 12px;
    font-size: 13px;
  }

  .triple-row {
    grid-template-columns: 1fr;
    gap: 10px;

    .ring-chart {
      width: 76px;
      height: 76px;
    }

    .ring-value {
      font-size: 14px;
    }

    .metric-label {
      font-size: 12px;
    }

    .metric-value {
      font-size: 13px;

      &.highlight {
        font-size: 14px;
      }
    }

    .metrics {
      gap: 10px;
    }

    .metric-row {
      gap: 20px;
    }

    .section-header {
      margin-bottom: 10px;
      padding-bottom: 8px;
    }
  }

  .section-card {
    padding: 12px 14px;
  }

  .section-header {
    margin-bottom: 12px;
    padding-bottom: 10px;
  }

  .section-num {
    font-size: 13px;
  }

  .section-title {
    font-size: 13px;
  }

  .metric-row {
    gap: 20px;
  }

  .ring-chart {
    width: 76px;
    height: 76px;
  }

  .ring-value {
    font-size: 14px;
  }

  .metrics {
    gap: 10px;
  }

  .metric-label {
    font-size: 12px;
  }

  .metric-value {
    font-size: 13px;

    &.highlight {
      font-size: 14px;
    }
  }

  .summary-grid {
    flex-direction: column;
    gap: 12px;
  }

  .summary-value {
    font-size: 15px;

    &.highlight {
      font-size: 18px;
    }
  }

  .warning-card {
    padding: 12px 14px;
  }

  .warning-content {
    padding-left: 0;
  }

  .warning-header {
    flex-wrap: wrap;
  }

  .veto-list li {
    font-size: 12px;
  }

  .tab-btn {
    padding: 8px 12px;
    font-size: 13px;
  }

  .tab-content {
    min-height: 150px;
    max-height: 280px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .table-count {
    font-size: 12px;
  }

  .score-table {
    min-width: 440px;
  }

  .score-table th,
  .score-table td {
    padding: 8px 8px;
    font-size: 11px;
  }

  .answer-cell {
    font-size: 11px;
  }

  .scale-tag {
    font-size: 16px;
  }

  .scale-name {
    font-size: 10px;
  }

  .scale-range {
    font-size: 10px;
  }

  .scale-item {
    padding: 12px 4px 10px;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  /* 规则与画像小屏样式 */
  .rules-image-wrapper {
    flex-direction: column;
    gap: 14px;
  }

  .rules-section,
  .image-section {
    flex: none;
  }

  .rules-section .scale-track {
    border-radius: 10px;
  }

  .scale-item {
    padding: 8px 3px;

    .scale-tag {
      font-size: 14px;
    }

    .scale-name {
      font-size: 9px;
      margin-bottom: 3px;
    }

    .scale-range {
      font-size: 8px;
    }
  }

  .image-section .profile-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .image-section .profile-card {
    padding: 10px 12px;
  }

  .logic-step {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }

  .logic-body {
    font-size: 12px;
  }
}

/* ============================================
   断点 4：< 400px（小屏手机）
   ============================================ */
@media screen and (max-width: 399px) {
  .scoring-detail-page {
    padding: 8px 8px;
    background: linear-gradient(180deg, #f0f4ff 0%, #faf5ff 50%, #f0f9ff 100%);
  }

  .content {
    gap: 8px;
  }

  .header {
    margin-bottom: 10px;

    h1 {
      font-size: 16px;
    }
  }

  .back-btn {
    padding: 5px 10px;
    font-size: 12px;
  }

  .triple-row {
    gap: 8px;

    .ring-chart {
      width: 64px;
      height: 64px;
    }

    .ring-value {
      font-size: 12px;
    }

    .metric-label {
      font-size: 11px;
    }

    .metric-value {
      font-size: 12px;

      &.highlight {
        font-size: 13px;
      }
    }

    .metrics {
      gap: 6px;
    }

    .metric-row {
      gap: 12px;
    }
  }

  .section-card {
    padding: 10px 10px;
  }

  .section-header {
    margin-bottom: 10px;
    padding-bottom: 8px;
    gap: 6px;
  }

  .ring-chart {
    width: 64px;
    height: 64px;
  }

  .ring-value {
    font-size: 12px;
  }

  .metrics {
    gap: 6px;
  }

  .metric-label {
    font-size: 11px;
  }

  .metric-value {
    font-size: 12px;

    &.highlight {
      font-size: 13px;
    }
  }

  .metric-row {
    gap: 12px;
  }

  .summary-value {
    font-size: 14px;

    &.highlight {
      font-size: 16px;
    }
  }

  .tab-btn {
    padding: 7px 10px;
    font-size: 12px;
  }

  .tab-content {
    max-height: 240px;
  }

  .score-table {
    min-width: 360px;
  }

  .score-table th,
  .score-table td {
    padding: 6px 6px;
    font-size: 10px;
  }

  .answer-cell {
    font-size: 10px;
  }

  .warning-card {
    padding: 10px 12px;
  }

  .veto-list {
    padding-left: 14px;
  }

  .scale-tag {
    font-size: 16px;
  }

  .scale-name {
    font-size: 10px;
  }

  .scale-range {
    font-size: 10px;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  /* 规则与画像小屏手机样式 */
  .rules-image-wrapper {
    flex-direction: column;
    gap: 12px;
  }

  .rules-section,
  .image-section {
    flex: none;
  }

  .rules-section .scale-track {
    border-radius: 8px;
  }

  .scale-item {
    padding: 6px 2px;

    .scale-tag {
      font-size: 12px;
    }

    .scale-name {
      font-size: 8px;
      margin-bottom: 2px;
    }

    .scale-range {
      font-size: 7px;
    }
  }

  .image-section .profile-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .image-section .profile-card {
    padding: 8px 10px;
  }

  .logic-step {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }

  .logic-body {
    font-size: 12px;
  }
}
</style>
